/**
 * What `POST /api/meetings/request` answered. The server answers HTTP 200 for `ok`, `refused` and `failed` alike
 * (VMR #35363), so the status alone reads a failed Join as a success: this reads `result`. A 409's code comes
 * through the proxy as `error` (`too_late`: the Undo came after the 5 s hold; `starting`: her start has not
 * written its record yet). Import-free so jest can run it.
 */

export type MeetingAnswer =
  | { kind: 'ok'; leaveAt?: number }
  | { kind: 'refused'; reason?: string }
  | { kind: 'failed' }
  | { kind: 'tooLate' }
  | { kind: 'starting' };

/** The 409 codes the request route passes through to the client. */
export const MEETING_REQUEST_ERRORS = ['too_late', 'starting'] as const;

export function readMeetingAnswer(status: number, body: unknown): MeetingAnswer {
  const fields = body && typeof body === 'object' ? (body as Record<string, unknown>) : {};
  if (status < 200 || status >= 300) {
    if (status === 409 && fields.error === 'too_late') return { kind: 'tooLate' };
    if (status === 409 && fields.error === 'starting') return { kind: 'starting' };
    return { kind: 'failed' };
  }
  if (fields.result === 'failed') return { kind: 'failed' };
  if (fields.result === 'refused') return typeof fields.refused === 'string' ? { kind: 'refused', reason: fields.refused } : { kind: 'refused' };
  const leaveAt = typeof fields.leaveAt === 'string' ? Date.parse(fields.leaveAt) : NaN;
  return Number.isFinite(leaveAt) ? { kind: 'ok', leaveAt } : { kind: 'ok' };
}

/** The server's refusal codes (`TAP_REFUSED` in `VideoMeetingScreens.java`) and the line each one shows. */
const REFUSAL_LINES: Record<string, string> = {
  not_configured: 'switched_off',
  not_allowlisted: 'switched_off',
  bad_link: 'bad_link',
  not_connected: 'not_connected',
  policy_unknown: 'policy_unknown',
  not_requested: 'not_requested',
  skipped_by_user: 'skipped',
  no_recipient: 'no_recipient',
  declined: 'declined',
  not_attendee: 'not_attendee',
  already_in_meeting: 'already_in',
};

export function meetingRefusalKey(reason: string | undefined): string {
  return `connectors.meetings.refused.${(reason && REFUSAL_LINES[reason]) || 'other'}`;
}

export type MeetingDecision = 'join' | 'skip' | 'remove' | 'keep' | null;

export interface MeetingNotice {
  /** An i18n key; `{agent}` is the agent's name. */
  message: string;
  severity: 'info' | 'error';
  /** Offer Undo: it sends `keep`. */
  undo?: true;
  /** Offer Try Again: it sends the same decision again. */
  retry?: true;
}

const GENERIC: MeetingNotice = { message: 'common.error.generic', severity: 'error' };

/**
 * The toast an answer shows, or null for none. An Add is only shown as done once the server confirmed it (the
 * close-out's "Add fails" rule), so a failed one says so and offers Try Again. Only a Remove during the meeting
 * offers Undo, for the 5 s the server holds her leave (`leaveAt`, spec P0-3). Before the meeting the row goes
 * straight back to Add, so there is nothing to undo.
 */
export function meetingNotice(decision: MeetingDecision, answer: MeetingAnswer): MeetingNotice | null {
  if (answer.kind === 'ok') {
    if (decision !== 'remove') return null;
    return answer.leaveAt === undefined
      ? { message: 'connectors.meetings.removed.before', severity: 'info' }
      : { message: 'connectors.meetings.removed.live', severity: 'info', undo: true };
  }
  if (answer.kind === 'refused') return { message: meetingRefusalKey(answer.reason), severity: 'error' };
  if (answer.kind === 'tooLate') return { message: 'connectors.meetings.tooLate', severity: 'error' };
  if (answer.kind === 'starting') return { message: 'connectors.meetings.starting', severity: 'error' };
  if (decision === 'join') return { message: 'connectors.meetings.addFailed', severity: 'error', retry: true };
  return GENERIC;
}
